import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Community, Domain, Question, QuestionAnswer} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {AuthService} from '../../_services/auth.service';
import {SnackbarService} from '../../_services/snackbar.service';
import {finalize, Observable, of, switchMap, take, tap} from 'rxjs';
import {Router} from '@angular/router';
import {CommunitiesService} from '../../_services/communities.service';
import {UserWithModerationInfo} from "../../_types/user";

@Component({
    selector: 'app-onboarding-questions',
    templateUrl: './onboarding-questions.component.html',
    styleUrl: './onboarding-questions.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OnboardingQuestionsComponent implements OnChanges, AfterContentChecked {
    @Input() community: Community | null | undefined = this.selectedCommunity;
    @Input() showHeader: boolean = true;
    @Input() domains: Domain[] = [];
    @Input() email: string | undefined;

    @Output() onSuccessEligibilityCheck: EventEmitter<void> = new EventEmitter<void>();
    @Output() storeEligibilityAnswers: EventEmitter<QuestionAnswer[]> = new EventEmitter<QuestionAnswer[]>();
    @Output() userProfilingRequired: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() setUserEligibilityEmail: EventEmitter<string | null> = new EventEmitter<string | null>();

    public eligibilityForm: FormGroup<{
        [key: string]: FormControl<string | Date | boolean | null>
    }>;
    public dynamicFormFields: Question[] = [];
    public loading: boolean = true;
    public companyImg: string = localStorage.getItem('communityLogo') || 'assets/explainLogoPlaceholder.png';
    public companyName: string = localStorage.getItem('communityName') || 'Explain'
    public today: Date = new Date();
    domainControl: any;

    private isCompanyAlreadyLoaded: boolean = false;

    constructor(
        private readonly fb: FormBuilder,
        private readonly localStorageService: LocalStorageService,
        private readonly authService: AuthService,
        private readonly snackbarService: SnackbarService,
        private readonly communitiesService: CommunitiesService,
        private readonly router: Router,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        this.eligibilityForm = this.fb.group({});
    }

    get isRegistrationPage(): boolean {
        return this.router.url.includes('/login');
    }

    get selectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['community']?.currentValue || changes['domains']?.currentValue) {
            this.tryReloadCommunityData();
        }
        if (changes['email']?.currentValue) {
            if (this.domainControl) {
                this.domainControl.setValue(this.email);
                this.domainControl.disable();
            }
        }
    }

    ngAfterContentChecked(): void {
        if (!this.isCompanyAlreadyLoaded) {
            this.tryReloadCommunityData();
        }
    }

    checkEligibility(): void {
        if (!this.community || this.loading) return;

        this.loading = true;
        let answers: QuestionAnswer[] = [];

        for (let field of this.dynamicFormFields) {
            if (!field.hidden && field.question_type != 'domain_check') {
                let value = this.eligibilityForm.get(field.id.toString())!.value;
                if (value === null || value === undefined) {
                    continue;
                }
                let answer = value;
                if (answer instanceof Date) {
                    answer = answer.toLocaleDateString('en-CA').slice(0, 10);
                }
                switch (field.question_type) {
                    case "single_choice":
                        answers.push({
                            question: field.id,
                            options: [answer]
                        })
                        break;
                    case "multiple_choice":
                        answers.push({
                            question: field.id,
                            options: answer
                        })
                        break;
                    case "date":
                        answers.push({
                            question: field.id,
                            answer_date: answer
                        })
                        break;
                    case "text":
                        answers.push({
                            question: field.id,
                            answer_text: answer
                        })
                        break;
                }
            }
        }
        this.authService.checkEligibility(this.community!.id, answers).pipe(
            switchMap(r => {
                this.snackbarService.openSuccessSnackBar(r.message);
                this.onSuccessEligibilityCheck.emit();
                this.storeEligibilityAnswers.emit(answers);

                return this.authService.getUser() ? this.reCheckUserFinishRegistration() : of(null);
            }),
            finalize((): void => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
            }),
            take(1)
        ).subscribe({
            error: e => {
                this.loading = false;
                this.snackbarService.openErrorSnackBar('Unfortunately you are not eligible to join this community')
            }
        })
    }

    fieldSelect(fieldValue: any, field: any): void {
        for (let option of field.options) {
            if (field.question_type == 'multiple_choice' && fieldValue.value && fieldValue.value?.includes(option.id) && option.is_exclusive) {
                this.eligibilityForm.get(field.id.toString())!.setValue([option.id]);
                return
            }
        }
    }

    private tryReloadCommunityData(): void {
        if (this.community?.id) {
            this.loading = true;
            this.isCompanyAlreadyLoaded = true; // This flag only for skip initial loading(for separate page logic)
            this.companyImg = this.community.logo;
            this.companyName = this.community.name;


            this.authService.getCommunityOnboardingQuestions(this.community?.id).pipe(take(1)).subscribe(res => {
                const fields: Question[] = [...res.results];
                this.eligibilityForm = this.fb.group({});
                this.domainControl = null;

                if (this.domains.length > 0) {
                    fields.unshift({
                        hidden: false,
                        id: 0,
                        is_dob: false,
                        options: [],
                        text: this.community?.custom_eligibility_email_input_text ? this.community?.custom_eligibility_email_input_text : 'What is your email?',
                        question_type: 'domain_check'
                    })
                }
                for (let response of fields) {
                    if (!response.hidden) {
                        let validators = response.question_type == 'domain_check' ? [Validators.required, Validators.email] : [Validators.required]
                        let control: FormControl<string | null> = new FormControl('', validators);
                        this.eligibilityForm.addControl(`${response.id}`, control);
                        if (response.question_type == 'domain_check') {
                            control.valueChanges.subscribe(
                                (value): void => {

                                    const email: string = value?.trim().toLowerCase() ?? '';
                                    const emailDomain: string = email.split('@')[1];

                                    const matchesDomain: boolean = this.domains.some(
                                        d => d.domain.toLowerCase() === emailDomain
                                    );

                                    this.userProfilingRequired.emit(!matchesDomain);
                                    this.setUserEligibilityEmail.emit(value);
                                }
                            );

                            this.domainControl = control;
                            if (this.email) {
                                control.setValue(this.email);
                                control.disable();
                            }
                        }
                    }
                }
                this.dynamicFormFields = fields;
                this.loading = false;
                this.changeDetectorRef.detectChanges();
            }, e => {
                this.loading = false;
                this.snackbarService.openErrorSnackBar('Error receiving community onboarding questions please try again later');
            })
        } else {
            this.loading = false;
        }
    }


    private reCheckUserFinishRegistration(): Observable<UserWithModerationInfo | null> {
        return this.communitiesService.getUserInformationForCommunity(this.community!.id).pipe(
            tap((res): void => {
                this.communitiesService.profileRefreshed = !res ? true : (res?.profile_refreshed === null || !!res?.profile_refreshed);
                if (this.communitiesService.profileRefreshed || !this.community?.questions?.length) {
                    this.snackbarService.openSuccessSnackBar('You successfully finished onboarding');
                    this.router.navigate(['/']).then();
                    return;
                }
                this.snackbarService.openErrorSnackBar('Onboarding in selected community not finished');
                window.location.reload();
                return;
            })
        )
    }
}
